import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useTranslation } from "../../../../i18n";
import { useCallback, useEffect, useState } from "react";
import { InspectionLotInput } from "../../../../__generated__/types-and-hooks";

export interface BulkEditSkuModalProps {
  inspectionLots: InspectionLotInput[];
  open: boolean;
  onClose: () => void;
  onSave: (inspectionLots: InspectionLotInput[]) => void;
}

export default function BulkEditSkuModal({
  inspectionLots,
  open,
  onClose,
  onSave,
}: BulkEditSkuModalProps) {
  const t = useTranslation();
  const [lotSize, setLotSize] = useState<number>(100);
  const [inspectionLevel, setInspectionLevel] = useState<string>("GI");

  useEffect(() => {
    if (!open) {
      setLotSize(100);
      setInspectionLevel("GI");
    }
  }, [open]);

  const handleSave = useCallback(() => {
    onSave(
      inspectionLots.map((inspectionLot) => ({
        ...inspectionLot,
        lotSize,
        inspectionLevel,
      }))
    );
  }, [onSave, inspectionLots, lotSize, inspectionLevel]);

  const handleLotSizeChange = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      const num = parseInt(e.target.value, 10);
      setLotSize(num);
    },
    [setLotSize]
  );

  const handleInspectionLevel = useCallback(
    (
      event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
      value: string | null
    ) => {
      setInspectionLevel(value ?? "GI");
    },
    [setInspectionLevel]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Add SKU to inspection")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>
                <Typography level="body-sm">{t("SKUs")}</Typography>
              </FormLabel>
              {inspectionLots.map((lot) => (
                <Chip key={lot.sku}>{lot.sku}</Chip>
              ))}
            </FormControl>
            <FormControl>
              <FormLabel>
                <Typography level="body-sm">{t("Lot Size")}</Typography>
              </FormLabel>
              <Input
                type="number"
                // min={0}
                // max={1000000}
                onChange={handleLotSizeChange}
                onBlur={handleLotSizeChange}
                value={lotSize}
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                <Typography level="body-sm">{t("Inspection Level")}</Typography>
              </FormLabel>
              <Select onChange={handleInspectionLevel} value={inspectionLevel}>
                <Option value={"GI"}>GI</Option>
                <Option value={"GII"}>GII</Option>
                <Option value={"GIII"}>GIII</Option>
                <Option value={"S1"}>S1</Option>
                <Option value={"S2"}>S2</Option>
                <Option value={"S3"}>S3</Option>
                <Option value={"S4"}>S4</Option>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={inspectionLots.length === 0}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
